import { debounce, filter, values } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col } from 'react-bootstrap';
import { WithTranslation, withTranslation } from 'react-i18next';
import ReactTable, {
    Column,
    FinalState,
    RowInfo,
    SortingRule
} from 'react-table';
import {
    buildJobExportSettings,
    buildJobSearchBody,
    getFSEUsers,
    getJobs,
    setTableFilter,
    updateSelectedJob
} from '../../actions/manageJobActions';
import {
    Ifacility,
    Ijob,
    IjobWorkOrder,
    ImanageJobReducer,
    Ioption,
    Itile
} from '../../models';
import {
    jobStatusEnumForFilter,
    jobTypesIdEnum,
    jobTypesIdEnumInverse
} from '../../models-enums';
import { initialJob, initialUser } from '../../reducers/initialState';

import moment from 'moment';
import queryString from 'query-string';
import { FieldConfig } from 'react-reactive-form';
import { connect, useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { closeAllModals, getSAPJobMapping } from '../../actions/commonActions';
import { loadCountries } from '../../actions/countriesActions';
import { constants } from '../../constants/constants';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import { getCountries } from '../../reducers/countriesReducer';
import {
    selectActiveFseUsersByID,
    selectJobTableFilters,
    selectJobWorkOrdersWithWorkOrders
} from '../../reducers/manageJobReducer';
import { getActiveLocalCountry } from '../../reducers/manageUserReducer';
import {
    getUsersActiveCountry,
    getUsersCountries
} from '../../reducers/userReducer';
import { ReportLinkCell } from '../common/ReportLinkCell';
import { openJobReport } from '../../actions/manageReportActions';
import { Banner } from '../common/Banner';
import ExportToExcelButton, {
    ExportRequest
} from '../common/ExportToExcelButton';
import { FormUtil } from '../common/FormUtil';
import { addQuery } from '../common/OtherUtils';
import SearchTableForm from '../common/SearchTableForm';
import API from '../../constants/apiEndpoints';
import { TableUtil } from '../common/TableUtil';
import { capWords } from '../../helpers/stringHelpers';
import EditJobModal from './EditJobModal';
import { JobWorkOrderExpanderContainer } from './JobWorkOrderExpanderContainer';
import {
    nextSortState,
    SortableColumnHeader,
    SortState
} from '../common/SortableColumnHeader';

const uuidv4 = require('uuid/v4');

interface RowInfoJob extends RowInfo {
    original: Ijob;
}

export enum manageJobQueryParamsEnum {
    selectedJobID = 'selectedJobID'
}

type Iprops = RouteComponentProps<any>;

interface IdispatchProps {
    // Add your dispatcher properties here
    getJobs: typeof getJobs;
    getFSEUsers: typeof getFSEUsers;
    closeAllModals: typeof closeAllModals;
    jobManage: ImanageJobReducer;
    showEditJobModal: boolean;
    setTableFilter: typeof setTableFilter;
    tableData: Ijob[];
    jobTypes: any[];
    loading: boolean;
    facilities: { [key: string]: Ifacility };
    jobWorkOrdersByID: { [key: string]: IjobWorkOrder };
    queryParams: typeof manageJobQueryParamsEnum;
    updateSelectedJob: typeof updateSelectedJob;
    getSAPJobMapping: typeof getSAPJobMapping;
    openJobReport: typeof openJobReport;
}

interface Istate {
    selectedRow: any;
}

const ManageJob: FC<Iprops & IdispatchProps & WithTranslation> = props => {
    const { t, i18n } = props;
    const currentTile: Itile = constants.getTileByURL(props.location.pathname);
    const [state, setState] = useState<Istate>({
        selectedRow: []
    });
    const fseUsersByID = useSelector(selectActiveFseUsersByID);
    const tableFilters = useSelector(selectJobTableFilters);
    const usersActiveCountry = useSelector(getUsersActiveCountry);
    const usersActiveLocalCountry = useSelector(getActiveLocalCountry);
    const usersCountries = useSelector(getUsersCountries);
    const getActiveCountry = () => {
        if (usersActiveLocalCountry !== '') {
            return usersActiveLocalCountry;
        } else {
            return usersActiveCountry;
        }
    };

    const facilities: { [key: string]: Ifacility } = useMemo(() => {
        const data = props.tableData.reduce(
            (carry: { [key: string]: Ifacility }, job: Ijob) => {
                if (!job.facility) {
                    return carry;
                }

                return {
                    ...carry,
                    [job.facility.id]: job.facility
                };
            },
            {}
        );

        return data;
    }, [props.tableData]);

    const sort: SortState = {
        column: tableFilters.sortColumn ?? null,
        descending: !!tableFilters.sortDescending
    };

    const handleSort = (columnKey: string) => {
        const next = nextSortState(sort, columnKey);
        props.setTableFilter({
            sortColumn: next.column,
            sortDescending: next.descending,
            page: 1
        });
        setState({ selectedRow: {} });
    };

    const coulumns: any[] = useMemo(() => {
        return TableUtil.translateHeaders(
            [
                {
                    id: 'expander-toggle',
                    expander: true,
                    Expander: TableUtil.expanderToggle,
                    style: {
                        cursor: 'pointer',
                        textAlign: 'left',
                        userSelect: 'none'
                    }
                },
                {
                    id: 'status',
                    Header: 'status',
                    accessor: (job: Ijob) => {
                        return job.status === 'InProgress'
                            ? 'WIP'
                            : job.status === 'ReviewPending'
                            ? String(props.t('nsJob:reviewPending'))
                            : job.status;
                    },
                    width: 100
                },
                {
                    Header: 'type',
                    id: 'type',
                    accessor: ({ jobTypeID }: Ijob) =>
                        props.t(
                            jobTypesIdEnumInverse[
                                jobTypeID as keyof typeof jobTypesIdEnumInverse
                            ]
                        ),
                    width: 115
                },
                {
                    id: 'jobNumber',
                    Header: (
                        <SortableColumnHeader
                            label={String(props.t('jobNumber'))}
                            columnKey="jobNumber"
                            sort={sort}
                            onSort={handleSort}
                        />
                    ),
                    accessor: ({ jobNumber }: Ijob) =>
                        jobNumber ? jobNumber : '',
                    width: 120
                },
                {
                    id: 'facility',
                    Header: (
                        <SortableColumnHeader
                            label={String(props.t('facility'))}
                            columnKey="facility"
                            sort={sort}
                            onSort={handleSort}
                        />
                    ),
                    accessor: (job: Ijob) => {
                        return job?.facility?.name;
                    },
                    minWidth: 200
                },
                {
                    id: 'fseLead',
                    Header: (
                        <SortableColumnHeader
                            label={String(props.t('fseLead'))}
                            columnKey="fseLead"
                            sort={sort}
                            onSort={handleSort}
                        />
                    ),
                    Cell: (row: RowInfoJob) => {
                        const fseLead =
                            fseUsersByID[row.original.assignedUserID] ||
                            initialUser;

                        return (
                            <span>
                                {fseLead.first} {fseLead.last}
                            </span>
                        );
                    }
                },
                {
                    Header: (
                        <SortableColumnHeader
                            label={String(props.t('start date'))}
                            columnKey="startDate"
                            sort={sort}
                            onSort={handleSort}
                        />
                    ),
                    accessor: ({ startDate }: Ijob) => {
                        return startDate
                            ? moment
                                  .utc(startDate)
                                  .local(true)
                                  .format('DD-MMM-YY')
                            : 'n/a';
                    },
                    id: 'startDate',
                    width: 130
                },
                {
                    Header: (
                        <SortableColumnHeader
                            label={String(props.t('#WO'))}
                            columnKey="wos"
                            sort={sort}
                            onSort={handleSort}
                        />
                    ),
                    accessor: ({ id }: Ijob) => {
                        const jobWorkOrders = filter(props.jobWorkOrdersByID, {
                            jobID: id,
                            isDeleted: false
                        });
                        if (jobWorkOrders) {
                            return jobWorkOrders.length;
                        } else {
                            return 0;
                        }
                    },
                    id: 'wos',
                    width: 60
                },
                {
                    id: 'reportLink',
                    Header: 'report',
                    sortable: false,
                    width: 70,
                    Cell: (row: RowInfoJob) => (
                        <ReportLinkCell
                            hasReport={row.original.hasReport}
                            canPreviewReport={row.original.canPreviewReport}
                            t={props.t}
                            onOpen={() =>
                                props.openJobReport(props.t, {
                                    jobID: row.original.id,
                                    hasReport: row.original.hasReport,
                                    reportDocumentID:
                                        row.original.reportDocumentID,
                                    reportVersionID:
                                        row.original.reportVersionID
                                })
                            }
                        />
                    )
                }
            ],
            props.t
        );
        // eslint-disable-next-line
    }, [facilities, sort.column, sort.descending]);

    const dispatch = useDispatch();
    const countries = useSelector(getCountries);

    React.useEffect(() => {
        loadCountries(dispatch, countries);
        props.getSAPJobMapping();
        props.getFSEUsers();
        // eslint-disable-next-line
    }, []);

    const userOptions: Ioption[] = useMemo(() => {
        return Object.values(fseUsersByID)?.map(user => {
            const name = `${user.first} ${user.last}`;
            return { label: name, value: user.id };
        });
    }, [fseUsersByID]);

    const searchFieldConfig: FieldConfig = useMemo(() => {
        const countriesOptions: Ioption[] = countries
            .filter(c => usersCountries?.includes(c.id))
            .map(c => ({ label: c.name, value: c.id }));

        const defaultStatus = FormUtil.convertToSingleOption({
            id: jobStatusEnumForFilter.Active.toString(),
            label: props.t('Active')
        });

        const {
            startDate,
            endDate,
            type,
            statusFilter = defaultStatus,
            search,
            showAdditionalFilters,
            country,
            assignedUser,
            isDeleted
        } = tableFilters;
        const disabled = false;

        const mainSearchControls = {
            country: {
                render: FormUtil.Select,
                meta: {
                    options: countriesOptions,
                    label: t('manageFacility:country'),
                    colWidth: 4,
                    colWidthMedium: 4,
                    colWidthLarge: 4,
                    placeholder: 'common:searchPlaceholder',
                    name: 'country',
                    defaultValue: country,
                    isClearable: true
                },
                formState: {
                    value: country,
                    disabled
                }
            },
            search: {
                render: FormUtil.TextInputWithoutValidation,
                meta: {
                    label: t('common:search'),
                    colWidth: 5,
                    colWidthLarge: 5,
                    type: 'text',
                    placeholder: t('searchPlaceholder')
                },
                formState: {
                    value: search,
                    disabled
                }
            },
            type: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('manageMeasurementPointLists:type'),
                    options: FormUtil.convertEnumToOptions(
                        jobTypesIdEnum
                    ).filter(
                        option => option.value !== jobTypesIdEnum.Maintenance
                    ),
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: t('typePlaceholder'),
                    isClearable: true,
                    shouldTranslate: true
                },
                formState: {
                    value: type,
                    disabled
                }
            },
            statusFilter: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    label: t('status'),
                    options: FormUtil.convertEnumToOptions(
                        jobStatusEnumForFilter
                    ),
                    colWidth: 4,
                    colWidthLarge: 3,
                    type: 'select',
                    placeholder: t('statusPlaceholder'),
                    shouldTranslate: true
                },
                formState: {
                    value: statusFilter,
                    disabled
                }
            },
            assignedUser: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: userOptions,
                    label: t('FSENameTableHeader'),
                    colWidth: 3,
                    isClearable: true,
                    shouldTranslate: true,
                    dataType: 'fseUser'
                },
                formState: { value: assignedUser || '', disabled }
            },
            isDeleted: {
                render: FormUtil.Toggle,
                meta: {
                    label: t('showDeleted'),
                    colWidth: 2,
                    innerStyle: {
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        alignItems: 'center',
                        height: '48px',
                        paddingTop: '24px'
                    }
                },
                formState: {
                    value: isDeleted || false,
                    disabled
                }
            }
        };
        const additionalSearchControls = {
            startDate: {
                render: FormUtil.Datetime,
                meta: {
                    label: t('startDate'),
                    colWidth: 3,
                    colWidthLarge: 3,
                    showTime: false,
                    placeholder: t('common:datePlaceholder')
                },
                formState: {
                    value: startDate || '',
                    disabled
                },
                options: {
                    validators: FormUtil.validators.isValidMoment
                }
            },
            endDate: {
                render: FormUtil.Datetime,
                meta: {
                    label: t('endDate'),
                    colWidth: 3,
                    colWidthLarge: 3,
                    showTime: false,
                    placeholder: t('common:datePlaceholder')
                },
                formState: {
                    value: endDate || '',
                    disabled
                },
                options: {
                    validators: FormUtil.validators.isValidMoment
                }
            }
        };

        if (showAdditionalFilters) {
            return {
                controls: {
                    ...mainSearchControls,
                    ...additionalSearchControls
                }
            };
        } else {
            return { controls: { ...mainSearchControls } };
        }
        // eslint-disable-next-line
    }, [
        tableFilters.showAdditionalFilters,
        countries,
        usersCountries,
        usersActiveCountry,
        usersActiveLocalCountry,
        userOptions,
        t,
        i18n.language
    ]);

    useEffect(() => {
        if (
            tableFilters.country === null ||
            tableFilters.country === undefined
        ) {
            const activeCountryID = getActiveCountry();
            const activeCountryOption: Ioption = {
                value: activeCountryID,
                label: countries.find(c => c.id === activeCountryID)?.name || ''
            };

            props.setTableFilter({
                country: activeCountryOption
            });
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        let activeCountryID: string | undefined = undefined;

        if (tableFilters.country) {
            activeCountryID = tableFilters.country.value;
        }

        props.getJobs(activeCountryID);

        // scroll top every time a filter changes
        const tableDiv = document.getElementsByClassName('rt-tbody');
        if (tableDiv && tableDiv.length) {
            tableDiv[0].scrollTop = 0;
        }
        // eslint-disable-next-line
    }, [tableFilters]);

    const handleSelectJob = (id: string) => {
        addQuery(manageJobQueryParamsEnum.selectedJobID, id, props.history);
    };

    const onPageChange = (page: number) => {
        const newPage = page + 1;
        props.setTableFilter({ page: newPage });
    };

    const onSearchValueChanges = useCallback(
        (value: any, key: string) => {
            props.setTableFilter({
                [key]: value
            });
        },
        // eslint-disable-next-line
        [tableFilters]
    );
    const debouncedSearchValueChanges = useCallback(
        debounce(onSearchValueChanges, 300),
        []
    );
    const onSortedChanged = (
        newSorted: SortingRule[],
        column: any,
        shiftKey: boolean
    ) => {
        props.setTableFilter({ sorted: newSorted });
        setState({ selectedRow: {} });
    };

    const hasActiveAdditionalFilters = () => {
        const { endDate, startDate } = tableFilters;
        if (endDate || startDate) {
            return true;
        } else {
            return false;
        }
    };

    const handleNewJobClick = () => {
        const id = uuidv4();
        props.updateSelectedJob({ ...initialJob, id });
        addQuery(manageJobQueryParamsEnum.selectedJobID, id, props.history);
    };

    /*
     * Builds the CSV export request at click time so it always reflects the
     * filters the user currently has applied. Column headers are the same
     * translated labels the grid shows (in display order); the server keeps
     * the field set/order fixed.
     */
    const buildExportRequest = (): ExportRequest => {
        const { t } = props;
        const activeCountryID = tableFilters.country
            ? tableFilters.country.value
            : undefined;
        return {
            url: API.job.exportCsv,
            body: {
                ...buildJobSearchBody(tableFilters, activeCountryID),
                // Translations still flow through t(); capWords() title-cases
                // each word so headers read consistently (e.g. "start date" ->
                // "Start Date"). Already-cased letters and acronyms (FSE/WO)
                // are left untouched.
                columnHeaders: [
                    // Job columns (collapsed grid)
                    capWords(t('status')),
                    capWords(t('type')),
                    capWords(t('jobNumber')),
                    capWords(t('facility')),
                    capWords(t('fseLead')),
                    capWords(t('start date')),
                    capWords(t('#WO')),
                    'Job ID',
                    // Flattened work-order columns (the job's expander) -
                    // prefixed so they're unambiguous next to the job columns
                    // (e.g. job "Status" vs work-order "Status").
                    `WO ${capWords(t('manageWorkOrder:number'))}`,
                    `WO ${capWords(t('manageWorkOrder:activity'))}`,
                    `WO ${capWords(t('manageWorkOrder:technician'))}`,
                    `WO ${capWords(t('manageWorkOrder:dueDate'))}`,
                    `WO ${capWords(t('status'))}`,
                    'Work Order ID'
                ],
                settings: buildJobExportSettings(t)
            },
            filename: 'jobs.csv'
        };
    };

    const getTdProps = (
        finalState: FinalState,
        rowInfo: RowInfoJob | undefined,
        column: Column | undefined,
        instance: any
    ) => {
        if (rowInfo && column) {
            const jobWorkOrders = filter(props.jobWorkOrdersByID, {
                jobID: rowInfo.original.id,
                isDeleted: false
            });

            return {
                onClick: (e: React.MouseEvent<HTMLFormElement>) => {
                    if (
                        column.id &&
                        (column.id === 'actions' || column.id === 'reportLink')
                    ) {
                        return;
                    }
                    if (jobWorkOrders.length === 0) {
                        setState({
                            selectedRow: rowInfo.index
                        });
                        handleSelectJob(rowInfo.original.id);
                        return;
                    }

                    setState({
                        selectedRow: {
                            [rowInfo.viewIndex || 0]: !state.selectedRow[
                                rowInfo.viewIndex || 0
                            ]
                        }
                    });
                },
                style: {
                    background: state.selectedRow[rowInfo.viewIndex]
                        ? constants.colors[
                              `${currentTile.color}Tr` as keyof typeof constants.colors
                          ]
                        : '',
                    opacity:
                        jobWorkOrders.length === 0 &&
                        column.id === 'expander-toggle'
                            ? '0'
                            : ''
                }
            };
        } else {
            return {};
        }
    };

    const tableClassName = tableFilters.showAdditionalFilters
        ? `beacon-table -highlight ${currentTile.color} additional-filters`
        : `beacon-table -highlight ${currentTile.color}`;

    return (
        <div className="manage-job">
            <Banner
                title={t('bannerTitle')}
                img={currentTile.srcBanner}
                color={currentTile.color}
            />
            <SearchTableForm
                fieldConfig={searchFieldConfig}
                handleSubmit={props.getJobs}
                loading={props.loading}
                showAdditionalFiltersButton={true}
                showAdditionalFilters={tableFilters.showAdditionalFilters}
                hasActiveAdditionalFilters={hasActiveAdditionalFilters()}
                colorButton={
                    constants.colors[
                        `${currentTile.color}Button` as keyof typeof constants.colors
                    ]
                }
                t={props.t}
                subscribeValueChanges={true}
                onValueChanges={debouncedSearchValueChanges}
                xs={9}
                lg={9}
            >
                <Col xs={2} lg={1}>
                    <Button
                        className="job-add-button"
                        bsStyle="link"
                        onClick={handleNewJobClick}
                        style={{ paddingTop: '29px' }}
                    >
                        {t('newJob')}
                    </Button>
                </Col>
                <ExportToExcelButton
                    getExportRequest={buildExportRequest}
                    inline
                />
            </SearchTableForm>

            <ReactTable
                data={props.tableData}
                onSortedChange={onSortedChanged}
                columns={coulumns}
                getTdProps={getTdProps}
                pageSize={
                    props.tableData.length >= 10 ? props.tableData.length : 10
                }
                page={tableFilters.page - 1}
                manual // Forces table not to paginate or sort automatically, so we can handle it server-side
                pages={props.jobManage.totalPages}
                showPageSizeOptions={false}
                className={tableClassName}
                previousText={t('common:previous')}
                nextText={t('common:next')}
                onPageChange={onPageChange}
                sortable={false}
                noDataText={t('common:noDataText')}
                resizable={false}
                loading={props.loading}
                expanded={state.selectedRow}
                SubComponent={(rowInfo: RowInfo) => {
                    return (
                        <JobWorkOrderExpanderContainer
                            {...rowInfo}
                            t={props.t}
                            fseUsersByID={fseUsersByID}
                            handleSelectJob={handleSelectJob}
                        />
                    );
                }}
            />
            <EditJobModal
                colorButton={
                    constants.colors[
                        `${currentTile.color}Button` as keyof typeof constants.colors
                    ]
                }
                facilities={Object.values(facilities)}
                history={props.history}
                queryParams={props.queryParams}
            />
        </div>
    );
};

const mapStateToProps = (state: IinitialState, ownProps: Iprops) => {
    const tableData = values(state.manageJob.jobsByID);
    const queryParams = queryString.parse(
        ownProps.location.search
    ) as typeof manageJobQueryParamsEnum;
    const jobWorkOrdersByID = selectJobWorkOrdersWithWorkOrders(state);

    return {
        user: state.user,
        jobManage: state.manageJob,
        loading: selectIsLoading(state),
        showEditJobModal: queryParams.selectedJobID?.length > 0,
        tableData,
        jobWorkOrdersByID,
        queryParams
    };
};

export default withTranslation('nsJob')(
    connect(mapStateToProps, {
        getJobs,
        getFSEUsers,
        closeAllModals,
        setTableFilter,
        updateSelectedJob,
        getSAPJobMapping,
        openJobReport
    })(ManageJob)
);
